
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, Appearance, StripeElements, StripeElementsOptions } from '@stripe/stripe-js';
import { useStripe, useElements, PaymentElement, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { makePayment, handlePaymentResult, handlePaymentError, validateCustomerSession } from '../paymentService';
import type { PaymentContext, PaymentCallbacks, UserClickTracking, StripeInitData } from '../paymentTypes';
import type { PaymentServiceOverrides } from './AdyenCheckoutForm';
import { buildStripeConfirmationTokenParams } from './buildStripeConfirmationTokenParams';
import { resolveIntentPaymentMethodTypes } from './resolveIntentPaymentMethodTypes';
import * as translator from '../../../utils/translator';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import PoweredByStripe from '../../../components/PoweredByStripe/PoweredByStripe';
import { toMinorUnits } from '../../../utils/helpers';
import './StripeCheckout.css';

interface StripeCheckoutFormProps {
    stripeInitData: StripeInitData;
    paymentContext: PaymentContext;
    callbacks: PaymentCallbacks;
    tracking?: UserClickTracking;
    serviceOverrides?: PaymentServiceOverrides;
    hideBillingCountry?: boolean;
}

// ============================================================================
// Stripe Promise Cache
// ============================================================================

let stripePromise: Promise<Stripe | null> | null = null;

const getStripe = (publicKey: string): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripe(publicKey);
  }
  return stripePromise;
};

// ============================================================================
// Appearance Configuration
// ============================================================================

const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#269c6e',
    colorText: '#1e2d50',
    colorDanger: '#e74c3c',
    fontFamily: 'Roboto, sans-serif',
    borderRadius: '5px',
  },
  rules: {
    '.Input': {
      border: '1px solid #d9dce1',
      borderRadius: '5px',
      padding: '12px',
      fontSize: '14px',
    },
    '.Input:focus': {
      borderColor: '#0075ff',
      boxShadow: '0 0 0 2px #99c2ff',
    },
    '.Label': {
      fontSize: '13px',
      fontWeight: '700',
      color: '#687282',
    },
  },
};

// ============================================================================
// Payment Method Types
// ============================================================================

// How long to wait for Stripe to apply an Elements options update before failing the attempt.
const ELEMENTS_UPDATE_TIMEOUT_MS = 10_000;

// Express Checkout opens a wallet sheet during submit and has no save checkbox, so its
// submits keep the full list instead of narrowing the Elements group.
type SubmitSource = 'payment_element' | 'express_checkout';

// ============================================================================
// Inner Form Component (needs Stripe context)
// ============================================================================

interface InnerFormProps {
  stripeInitData: StripeInitData;
  paymentContext: PaymentContext;
  callbacks: PaymentCallbacks;
  tracking?: UserClickTracking;
  serviceOverrides?: PaymentServiceOverrides;
  hideBillingCountry?: boolean;
}

const InnerForm: React.FC<InnerFormProps> = ({ stripeInitData, paymentContext, callbacks, tracking, serviceOverrides, hideBillingCountry }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { language } = paymentContext;

  const [isProcessing, setIsProcessing] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const isCollapsingRef = useRef(false);
  // Payment method type selected in the Payment Element, reported by its change event.
  const selectedPaymentTypeRef = useRef<string | null>(null);
  // Resolves the pending applyElementsPaymentMethodTypes call when Stripe fires 'update-end'.
  const resolveElementsUpdateRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!elements) {
      return;
    }
    elements.on('update-end', () => {
      resolveElementsUpdateRef.current?.();
      resolveElementsUpdateRef.current = null;
    });
  }, [elements]);

  /**
   * Sets the Elements group's payment method types and waits until Stripe has applied them,
   * so the next ConfirmationToken carries exactly these types — Stripe rejects confirming a
   * PaymentIntent whose payment_method_types differ from the token's.
   */
  const applyElementsPaymentMethodTypes = useCallback((stripeElements: StripeElements, paymentMethodTypes: string[]) => (
    new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        resolveElementsUpdateRef.current = null;
        reject(new Error(`Stripe Elements did not apply paymentMethodTypes [${paymentMethodTypes.join(', ')}] within ${ELEMENTS_UPDATE_TIMEOUT_MS}ms`));
      }, ELEMENTS_UPDATE_TIMEOUT_MS);

      resolveElementsUpdateRef.current = () => {
        clearTimeout(timeoutId);
        resolve();
      };
      stripeElements.update({ paymentMethodTypes });
    })
  ), []);


  // ============================================================================
  // Helpers
  // ============================================================================

  const navigateTo = useCallback((path: string) => navigate(path), [navigate]);

  // ============================================================================
  // Handle Stripe Element Load Error
  // ============================================================================

  const handleLoadError = useCallback((event: { elementType: string; error: any }) => {
    window.clientLogger.error('Stripe PaymentElement failed to load', event.error);
    MainTracker.track('custom', 'Stripe Element Load Error', { elementType: event.elementType, error: event.error?.message });
  }, []);

  // ============================================================================
  // Handle Submit
  // ============================================================================

  const handleSubmit = useCallback(async (source: SubmitSource = 'payment_element') => {
    if (!stripe || !elements || isProcessing) {
      return;
    }

    setIsProcessing(true);
    setIsAuthorized(false);

    const offeredPaymentMethodTypes = stripeInitData.gatewayResult.paymentMethods ?? [];
    const intentPaymentMethodTypes = source === 'express_checkout'
      ? offeredPaymentMethodTypes
      : resolveIntentPaymentMethodTypes({ offeredTypes: offeredPaymentMethodTypes, selectedType: selectedPaymentTypeRef.current });
    const shouldNarrowElements = intentPaymentMethodTypes.length < offeredPaymentMethodTypes.length;
    let shouldRestoreElementsTypes = false;

    try {
      // 1. Validate customer session
      const { country, hashID } = paymentContext;
      const relocationString = `/${country}/account/my-orders/${hashID}/details`;
  
      if (!serviceOverrides?.skipSessionValidation && !validateCustomerSession()) {
        navigate({ pathname: relocationString, search: 'showRedirectModal=true' });
        return;
      }

      // 2. Limit the Elements group to the chosen payment method (see resolveIntentPaymentMethodTypes).
      //    Done before submit, so submit and the ConfirmationToken both see the narrowed list.
      if (shouldNarrowElements) {
        shouldRestoreElementsTypes = true;
        await applyElementsPaymentMethodTypes(elements, intentPaymentMethodTypes);
      }

      // 3. Validate form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        callbacks.showErrorNotification(
          translator.translate(language.toLowerCase(), 'default', 'StickyNotification/danger_title'),
          submitError.message || translator.translate(language.toLowerCase(), 'payment', 'stripe/refusalCode/DEFAULT')
        );
        setIsProcessing(false);
        return;
      }

      // 4. Create confirmation token (pass country when Element hides billing country)
      const { error: pmError, confirmationToken } = await stripe.createConfirmationToken({
        elements,
        ...buildStripeConfirmationTokenParams({
          hideBillingCountry,
          countryId: stripeInitData.orderAddresses?.country_id,
          fallbackCountry: paymentContext.country,
        }),
      });
      if (pmError) {
        callbacks.showErrorNotification(
          translator.translate(language.toLowerCase(), 'default', 'StickyNotification/danger_title'),
          pmError.message || translator.translate(language.toLowerCase(), 'payment', 'stripe/refusalCode/DEFAULT')
        );
        setIsProcessing(false);
        return;
      }

      // 5. Check if payment method requires ID verification before making payment
      const pmPreview = confirmationToken.payment_method_preview;
      const paymentType = pmPreview.card?.wallet?.type ?? pmPreview.type;
      const requiresVerification = callbacks.paymentTypesRequiringIdVerification?.includes(paymentType);
      
      if (requiresVerification && !callbacks.isIdVerified) {
        callbacks.onIdVerificationRequired?.(paymentType);
        callbacks.showErrorNotification(
          translator.translate(language.toLowerCase(), 'default', 'StickyNotification/danger_title'),
          'Please complete the verification before proceeding with payment.'
        );
        setIsProcessing(false);
        return;
      }

      const result = await (serviceOverrides?.makePayment ?? makePayment)({
        gatewayState: { paymentMethod: { id: confirmationToken.id, type: paymentType } },
        context: paymentContext,
        provider: 'stripe',
        stripeParams: {
          confirmationToken: confirmationToken.id,
          // Must equal the Elements group's types when the token was created.
          paymentMethodTypes: intentPaymentMethodTypes,
        },
        tracking,
      });

      // Handle 3DS authentication
      if (result.providerResponse?.status === 'requires_action') {
        result.providerResponse.action = "requires_action"; // so the payment service will call the onPaymentAction callback

        callbacks.onPaymentAction = async () => {
          const { error: nextActionError, paymentIntent } = await stripe.handleNextAction({clientSecret: result.providerResponse.client_secret});

          if (nextActionError) {
            callbacks.showErrorNotification(
              translator.translate(language.toLowerCase(), 'default', 'StickyNotification/danger_title'),
              nextActionError.message || translator.translate(language.toLowerCase(), 'payment', 'stripe/refusalCode/DEFAULT')
            );
            setIsProcessing(false);
            setIsAuthorized(false);
            return;
          }

          if (['requires_capture', 'succeeded'].includes(paymentIntent?.status ?? '')) {
            callbacks.onPaymentSuccess(result);
            try {
              // return_url is a full URL, extract pathname and search params for React Router
              const url = new URL(result.return_url);
              navigate(url.pathname + url.search);  // navigate to the payment_page_callback
            } catch (e) {
              window.location.href = result.return_url; // Fallback: use window.location for full navigation
            }
          }
          else {
            result.resultCode = 'Refused';
          }
          // TODO -- change paymentIntent's status in the DB.
        };
      }

      handlePaymentResult({
        result,
        context: paymentContext,
        callbacks,
        navigate: navigateTo,
        onSuccessOverride: serviceOverrides?.onSuccess,
      });
      setIsAuthorized(result.isAuthorized ?? false);

    } catch (error: any) {
      handlePaymentError({ error, context: paymentContext, callbacks });
    } finally {
      if (shouldRestoreElementsTypes) {
        // Offer every method again so the shopper can retry with another one.
        elements.update({ paymentMethodTypes: offeredPaymentMethodTypes });
      }
      setIsProcessing(false);
    }
  }, [stripe, elements, paymentContext, callbacks, navigateTo, serviceOverrides, hideBillingCountry, stripeInitData, applyElementsPaymentMethodTypes]);

  // ============================================================================
  // Expose handleSubmit via callback
  // ============================================================================

  useEffect(() => {
    if (callbacks.onTriggerPaymentSubmit !== undefined) {
      // Store handleSubmit in the callback so parent can call it
      (callbacks as any).onTriggerPaymentSubmit = handleSubmit;
    }
  }, [handleSubmit, callbacks]);

  // ============================================================================
  // Collapse payment method when modal closes without verification
  // ============================================================================

  const prevCollapseKeyRef = useRef(callbacks.verificationCollapseKey ?? 0);

  useEffect(() => {
    const currentKey = callbacks.verificationCollapseKey ?? 0;
    if (currentKey > prevCollapseKeyRef.current) {
      prevCollapseKeyRef.current = currentKey;
      // Set guard to prevent onChange from re-opening the modal during collapse
      isCollapsingRef.current = true;
      // Collapse the Stripe PaymentElement accordion to deselect the payment method
      const paymentElement = elements?.getElement('payment');
      if (paymentElement) {
        (paymentElement as any).collapse();
      }
      // Clear guard after a short delay to allow onChange events to settle
      setTimeout(() => {
        isCollapsingRef.current = false;
      }, 500);
    }
  }, [callbacks.verificationCollapseKey, elements]);

  // ============================================================================
  // Render
  // ============================================================================

  // Prefill Link's returning-customer OTP flow / new-customer signup form.
  const billingDetails = {
    ...(stripeInitData.customerData?.email && { email: stripeInitData.customerData.email }),
    ...(stripeInitData.orderAddresses?.phone && { phone: stripeInitData.orderAddresses.phone }),
    ...((stripeInitData.customerData?.first_name || stripeInitData.customerData?.last_name) && {
      name: `${stripeInitData.customerData?.first_name ?? ''} ${stripeInitData.customerData?.last_name ?? ''}`.trim(),
    }),
  };

  return (
    <div className="stripe-checkout-container">
      {isAuthorized ? (
        <PaymentSuccessAnimation language={language} />
      ) : (
        <form
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="stripe-checkout-form"
        >
          {callbacks.paymentTypesRequiringIdVerification?.includes('paypal') && !callbacks.isIdVerified && !callbacks.hidePaypalDisclaimer && (
            <div className="paypal-verification-disclaimer">
              {translator.translate(language.toLowerCase(), 'payment', callbacks.isExpressOrder ? 'idVerificationDisclaimerExpress' : 'idVerificationDisclaimer')}
            </div>
          )}

          <div className="payment-elements-wrapper">
            <PaymentElement
              options={{
                layout: { type: 'accordion', defaultCollapsed: false },
                ...(Object.keys(billingDetails).length > 0 && { defaultValues: { billingDetails } }),
                ...(hideBillingCountry && { fields: { billingDetails: { address: { country: 'never' } } } }),
              }}
              onReady={() => setIsElementReady(true)}
              onChange={(event) => {
                const paymentType = event.value.type;
                selectedPaymentTypeRef.current = paymentType;
                const requiresVerification = callbacks.paymentTypesRequiringIdVerification?.includes(paymentType);
                if (requiresVerification && !isCollapsingRef.current && !callbacks.isIdVerified) {
                  callbacks.onIdVerificationRequired?.(paymentType);
                }
              }}
              onLoadError={handleLoadError}
            />

            <ExpressCheckoutElement
              options={{ paymentMethods: { applePay: 'always', googlePay: 'always', link: paymentContext.stripeLinkVariant === 'B' ? 'auto' : 'never' },
                         layout: { maxColumns: 1 , overflow: 'never' },
                         buttonTheme: { applePay: 'white-outline', googlePay: 'white' },
                         buttonHeight: 50}}
              onConfirm={() => handleSubmit('express_checkout')}
            />
          </div>

          <button type="submit" disabled={!stripe || !isElementReady || isProcessing} className="stripe-pay-button">
            {isProcessing ? (
              <>
                <span className="stripe-spinner"></span>
                <span>{translator.translate(language.toLowerCase(), 'payment', 'button/processing')}</span>
              </>
            ) : (translator.translate(language.toLowerCase(), 'payment', 'button/pay'))}
          </button>
        </form>
      )}
      {!isAuthorized && <PoweredByStripe />}
    </div>
  );
};

// ============================================================================
// Payment Success Animation
// ============================================================================

interface PaymentSuccessAnimationProps {
  language: string;
}

const PaymentSuccessAnimation: React.FC<PaymentSuccessAnimationProps> = ({ language }) => (
  <div className="stripe-success-animation">
    <div className="stripe-success-content">
      <div className="stripe-success-checkmark">
        <svg className="stripe-checkmark-svg" viewBox="0 0 52 52">
          <circle className="stripe-checkmark-circle" cx="26" cy="26" r="25" fill="none" />
          <path className="stripe-checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
        </svg>
      </div>
      <h2 className="stripe-success-title">{translator.translate(language.toLowerCase(), 'payment', 'success/title')}</h2>
      <p className="stripe-success-message">{translator.translate(language.toLowerCase(), 'payment', 'success/redirecting')}</p>
      <div className="stripe-success-loader">
        <div className="stripe-success-loader-bar"></div>
      </div>
    </div>
  </div>
);

// ============================================================================
// Main Component (with Elements wrapper)
// ============================================================================

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({stripeInitData, paymentContext, callbacks, tracking, serviceOverrides, hideBillingCountry}) => {
  const { stripePublicKey, customerSessionSecret, isSubscription } = stripeInitData;

  const amount = toMinorUnits(paymentContext.orderPrice, paymentContext.currencyRate);

  const options = {
    // payment Methods
    mode: 'payment',
    paymentMethodCreation: "manual",
    paymentMethodTypes: stripeInitData.gatewayResult.paymentMethods ?? [],
    
    // amount and currency
    amount: amount,
    currency: paymentContext.currency,

    // Must match PaymentIntent.setup_future_usage on the server (off_session for subscriptions)
    ...(isSubscription ? { setupFutureUsage: 'off_session' as const } : {}),

    // appearance
    appearance,
    locale: paymentContext.language, // for translations

    // keys
    customerSessionClientSecret: customerSessionSecret,
  } as StripeElementsOptions; 

  return (
    <Elements stripe={getStripe(stripePublicKey)} options={options}>
      <InnerForm
        stripeInitData={stripeInitData}
        paymentContext={paymentContext}
        callbacks={callbacks}
        tracking={tracking}
        serviceOverrides={serviceOverrides}
        hideBillingCountry={hideBillingCountry}
      />
    </Elements>
  );
};

export default StripeCheckoutForm;